import React, { useState, useEffect } from 'react';
import { useStyles } from './SessionsProcessSchedule.styles';
import { ExamTaskGroupProcessesSchedule, ExamTaskGroupProcessesScheduleUpdate } from './SessionsProcessSchedule.types';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { InputTableColumns, ErrorList } from '../../../../components/InputTable/InputTable.type';
import PickDateForProcess from './../../../../assets/icons/pickDateForProcess.svg';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import CloseIcon from '@material-ui/icons/Close';
import TooltipOne from '../../../../components/TooltipOne/TooltipOne.component';
import { DateTimePickerOneMenu } from '../../../../components/DateTimePickerOne/DateTimePickerOneMenu/DateTimePickerOneMenu.component';
import {
  getProcessesScheduleBySession,
  updateProcessesSchedule,
} from './../../../../services/exams/assessmentSession/assessments.service';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { getProcessesScheduleBySheetId } from '../../../../services/exams/assessmentSession/assessments.service';
import { AssessmentSessionProcessTypeId } from '../../../../services/exams/assessmentSession/assessments.types';
import { getProcessLabel } from '../../../../utils/assessmentSessionProcess';
import { getLocalISODate } from './../../../../utils/dateFunctionUtils';
import { countryFormat } from '../../../../const/formatDate';
import { OverflowTypograpy } from '../../../../components/OverflowTypography/OverflowTypography.component';
import { useTranslation } from 'react-i18next';

interface SessionsProcessScheduleProps {
  sheetId: string;
  isExaminerView: boolean;
  onClose: () => void;
  onChange?: (isDataChanged: boolean) => void;
}

type dateValidationErrorType =
  | 'trainingDateEmpty'
  | 'trainingTooEarly'
  | 'trainingTooLate'
  | 'qualificationDateEmpty'
  | 'qualificationTooEarly'
  | 'qualificationTooLate'
  | 'scoringDateEmpty'
  | 'scoringTooLate';

export const SessionsProcessSchedule: React.FC<SessionsProcessScheduleProps> = ({
  onClose,
  sheetId,
  isExaminerView,
  onChange,
}) => {
  const styles = useStyles();
  const { openAlert } = useLayoutContext();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [schedules, setSchedules] = useState<ExamTaskGroupProcessesSchedule[]>([]);
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const [edited, setEdited] = useState<boolean>(false);
  const [dateTimePickerAnchor, setDateTimePickerAnchor] = useState<HTMLDivElement | HTMLImageElement | undefined>(
    undefined,
  );
  const [activeSchedule, setActiveSchedule] = useState<ExamTaskGroupProcessesSchedule | undefined>(undefined);
  const [activeProcess, setActiveProcess] = useState<AssessmentSessionProcessTypeId | undefined>(undefined);

  const isDateCellDisabled = activeSchedule && !activeSchedule.assessmentSessionDateFrom

  const columns: InputTableColumns[] = [
    {
      id: 'examTaskGroupName',
      label: 'Nazwa grupy zadań',
    },
    {
      id: 'trainingAssessmentSessionDateFrom',
      label: (
        <div className={styles.headerCell}>
          {t('sessionsParameters:sessionsProcessSchedule.table.trainingAssessmentSessionDateFrom')}
        </div>
      ),
    },
    {
      id: 'qualificationAssessmentSessionDateFrom',
      label: (
        <div className={styles.headerCell}>
          {t('sessionsParameters:sessionsProcessSchedule.table.qualificationAssessmentSessionDateFrom')}
        </div>
      ),
    },
    {
      id: 'scoringAssessmentSessionDateFrom',
      label: (
        <div className={styles.headerCell}>
          {t('sessionsParameters:sessionsProcessSchedule.table.scoringAssessmentSessionDateFrom')}
        </div>
      ),
    },
  ];

  const validateSchedules = () => {
    const errorList: ErrorList[] = [];
    schedules.forEach((schedule: ExamTaskGroupProcessesSchedule) => {
      const errorsForSchedule: dateValidationErrorType[] = [];

      if (schedule.isTrainingAssessmentSessionRequired) {
        if (!schedule.trainingAssessmentSessionDateFrom) {
          errorsForSchedule.push('trainingDateEmpty');
        } else if (
          schedule.assessmentSessionDateFrom &&
          schedule.trainingAssessmentSessionDateFrom < schedule.assessmentSessionDateFrom
        ) {
          errorsForSchedule.push('trainingTooEarly');
        } else if (
          schedule.assessmentSessionDateTo &&
          schedule.trainingAssessmentSessionDateFrom > schedule.assessmentSessionDateTo
        ) {
          errorsForSchedule.push('trainingTooLate');
        }
      }
      if (!errorsForSchedule.length && schedule.isQualificationAssessmentSessionRequired) {
        if (!schedule.qualificationAssessmentSessionDateFrom) {
          errorsForSchedule.push('qualificationDateEmpty');
        } else if (
          schedule.isTrainingAssessmentSessionRequired &&
          schedule.trainingAssessmentSessionDateFrom &&
          schedule.qualificationAssessmentSessionDateFrom < schedule.trainingAssessmentSessionDateFrom
        ) {
          //todo add validation to < session start when training is not required
          errorsForSchedule.push('qualificationTooEarly');
        } else if (
          schedule.isScoringAssessmentSessionRequired &&
          schedule.scoringAssessmentSessionDateFrom &&
          schedule.qualificationAssessmentSessionDateFrom > schedule.scoringAssessmentSessionDateFrom
        ) {
          //todo add validation to > session end when scoring is not required
          errorsForSchedule.push('qualificationTooLate');
        }
      }
      if (!errorsForSchedule.length && schedule.isScoringAssessmentSessionRequired) {
        if (!schedule.scoringAssessmentSessionDateFrom) {
          errorsForSchedule.push('scoringDateEmpty');
        } else if (
          schedule.assessmentSessionDateTo &&
          schedule.scoringAssessmentSessionDateFrom > schedule.assessmentSessionDateTo
        ) {
          errorsForSchedule.push('scoringTooLate');
        }
      }
      errorsForSchedule.forEach((type: dateValidationErrorType) => {
        errorList.push({ type, id: schedule.examTaskGroupId });
      });
    });
    setInvalidType(errorList);
  };

  const getDateFormatted = (date: Date | null, withYear = false) => {
    if (date) {
      const dateObj = new Date(date);
      const dateDay = dateObj.toLocaleDateString(countryFormat, { day: '2-digit' });
      const dateMonth = dateObj.toLocaleDateString(countryFormat, { month: '2-digit' });
      const dateYear = withYear ? dateObj.toLocaleDateString(countryFormat, { year: 'numeric' }) : null;
      return (
        <>
          {dateDay}/{dateMonth}
          {dateYear ? <>/{dateYear}</> : null}
        </>
      );
    }
    return null;
  };

  const getTimeFormatted = (date: Date) => {
    if (date) {
      const dateObj = new Date(date);
      return dateObj.toLocaleTimeString(countryFormat, { hour: '2-digit', minute: '2-digit' });
    }
    return null;
  };

  const getDateByProcess = (
    schedule: ExamTaskGroupProcessesSchedule,
    process: AssessmentSessionProcessTypeId,
  ): Date | null => {
    switch (process) {
      case AssessmentSessionProcessTypeId.Training:
        return schedule.trainingAssessmentSessionDateFrom;
      case AssessmentSessionProcessTypeId.Qualification:
        return schedule.qualificationAssessmentSessionDateFrom;
      case AssessmentSessionProcessTypeId.Scoring:
        return schedule.scoringAssessmentSessionDateFrom;
      default:
        return null;
    }
  };

  const getProcessRequiredByProcess = (
    schedule: ExamTaskGroupProcessesSchedule,
    process: AssessmentSessionProcessTypeId,
  ): boolean => {
    switch (process) {
      case AssessmentSessionProcessTypeId.Training:
        return schedule.isTrainingAssessmentSessionRequired;
      case AssessmentSessionProcessTypeId.Qualification:
        return schedule.isQualificationAssessmentSessionRequired;
      case AssessmentSessionProcessTypeId.Scoring:
        return schedule.isScoringAssessmentSessionRequired;
      default:
        return false;
    }
  };

  const pickDateForProcess = (
    anchor: HTMLImageElement | HTMLDivElement,
    schedule: ExamTaskGroupProcessesSchedule,
    process: AssessmentSessionProcessTypeId,
  ) => {
    setDateTimePickerAnchor(anchor);
    setActiveSchedule(schedule);
    setActiveProcess(process);
  };

  const clearDatePickerData = () => {
    setDateTimePickerAnchor(undefined);
    setActiveSchedule(undefined);
    setActiveProcess(undefined);
  };

  const updateProcessDate = (
    newDate: Date | null,
    changedSchedule: ExamTaskGroupProcessesSchedule,
    process: AssessmentSessionProcessTypeId,
  ) => {
    setEdited(true);
    setSchedules((prevState) => {
      return prevState.map((schedule: ExamTaskGroupProcessesSchedule) => {
        if (schedule.examTaskGroupId === changedSchedule.examTaskGroupId) {
          switch (process) {
            case AssessmentSessionProcessTypeId.Training:
              schedule.trainingAssessmentSessionDateFrom = newDate;
              break;
            case AssessmentSessionProcessTypeId.Qualification:
              schedule.qualificationAssessmentSessionDateFrom = newDate;
              break;
            case AssessmentSessionProcessTypeId.Scoring:
              schedule.scoringAssessmentSessionDateFrom = newDate;
              break;
          }
        }
        return schedule;
      });
    });
  };

  useEffect(() => {
    validateSchedules();
  }, [schedules]);

  const getDateCell = (schedule: ExamTaskGroupProcessesSchedule, process: AssessmentSessionProcessTypeId) => {
    const currentDate = getDateByProcess(schedule, process);
    if (currentDate === null && !getProcessRequiredByProcess(schedule, process)) {
      return null;
    } else
      return (
        <div className={styles.dateCell}>
          {!currentDate ? (
            <TooltipOne
              title={`${
                isDateCellDisabled
                  ? 'Ustaw daty rozpoczęcia i zakończenia sesji oceniania przed wybraniem daty i godziny rozpoczęcia procesu.'
                  : ''
              }`}
              placement="top"
            >
              <div
                className={[styles.pickDateIcon, isDateCellDisabled ? styles.disabledIcon : ''].join(' ')}
                onClick={(event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
                  if (isDateCellDisabled) return;
                  pickDateForProcess(event.currentTarget.parentElement as HTMLDivElement, schedule, process);
                }}
              >
                {!currentDate && (
                  <img
                    src={PickDateForProcess}
                    alt={`Wybierz datę rozpoczęcia ${getProcessLabel(process).toProcess}`}
                  />
                )}
              </div>
            </TooltipOne>
          ) : (
            <>
              <div
                aria-label={`Wybierz datę rozpoczęcia ${getProcessLabel(process).toProcess}`}
                className={styles.dateAndTimeFormatted}
                onClick={(event: React.MouseEvent<HTMLImageElement, MouseEvent>) =>
                  pickDateForProcess(event.currentTarget.parentElement as HTMLDivElement, schedule, process)
                }
              >
                <div className={styles.dateFormatted}>{getDateFormatted(currentDate, true)}</div>,{' '}
                <div className={styles.timeFormatted}>{getTimeFormatted(currentDate)}</div>
              </div>
              <div style={{ display: 'inline-block', width: '20px' }}>
                <TooltipOne title="Usuń datę" placement="top">
                  <CloseIcon
                    className={styles.dateCloseButton}
                    aria-label={`Przycisk usuwający datę ${getProcessLabel(process).toProcess} dla grupy zadań ${
                      schedule.examTaskGroupName
                    }`}
                    onClick={() => updateProcessDate(null, schedule, process)}
                  />
                </TooltipOne>
              </div>
            </>
          )}
        </div>
      );
  };

  const getRowComponent = (type: string, data: ExamTaskGroupProcessesSchedule): React.ReactNode | string | null => {
    switch (type) {
      case 'examTaskGroupName':
        return (
          <>
            <OverflowTypograpy
              style={{
                paddingTop: '5px',
                display: 'inline-block',
                fontWeight: 'bolder',
                fontSize: '1rem',
                color: '#3C419A',
                width: '160px',
              }}
            >
              {data[type]}
            </OverflowTypograpy>
            <div className={styles.taskGroupDates}>
              {getDateFormatted(data.assessmentSessionDateFrom)} - {getDateFormatted(data.assessmentSessionDateTo)}
            </div>
          </>
        );
      case 'trainingAssessmentSessionDateFrom':
        return getDateCell(data, AssessmentSessionProcessTypeId.Training);
      case 'qualificationAssessmentSessionDateFrom':
        return getDateCell(data, AssessmentSessionProcessTypeId.Qualification);
      case 'scoringAssessmentSessionDateFrom':
        return getDateCell(data, AssessmentSessionProcessTypeId.Scoring);
    }
  };

  const getValidationMessage = (error: ErrorList): string => {
    switch (error.type) {
      case 'trainingDateEmpty':
        return 'Data rozpoczęcia procesu ćwiczeniowego jest wymagana';
      case 'trainingTooEarly':
        return 'Data rozpoczęcia procesu ćwiczeniowego nie może być wcześniejsza niż data rozpoczęcia sesji oceniania';
      case 'trainingTooLate':
        return 'Data rozpoczęcia procesu ćwiczeniowego nie może być późnijesza niż data zakończenia sesji oceniania';
      case 'qualificationDateEmpty':
        return 'Data rozpoczęcia procesu kwalifikacji jest wymagana';
      case 'qualificationTooEarly':
        return 'Data rozpoczęcia procesu kwalifikacji nie może być wcześniejsza niż data rozpoczęcia procesu ćwiczeniowego';
      case 'qualificationTooLate':
        return 'Data rozpoczęcia procesu kwalifikacji nie może być późnijesza niż data rozpoczęcia procesu oceniania';
      case 'scoringDateEmpty': {
        return `Data rozpoczęcia procesu oceniania jest wymagana. ${
          isDateCellDisabled
            ? 'Ustaw daty rozpoczęcia i zakończenia sesji oceniania przed wykonaniem tej operacji.'
            : ''
        }`;
      }
      case 'scoringTooLate':
        return 'Data rozpoczęcia procesu oceniania nie może być późniejesza niż data zakończenia sesji oceniania';
      default:
        return '';
    }
  };

  const handleSave = () => {
    const schedulesUpdateData: ExamTaskGroupProcessesScheduleUpdate[] = schedules.map(
      (schedule: ExamTaskGroupProcessesSchedule) => {
        return {
          examTaskGroupId: schedule.examTaskGroupId,
          qualificationAssessmentSessionDateFrom: schedule.qualificationAssessmentSessionDateFrom
            ? getLocalISODate(schedule.qualificationAssessmentSessionDateFrom)
            : null,
          trainingAssessmentSessionDateFrom: schedule.trainingAssessmentSessionDateFrom
            ? getLocalISODate(schedule.trainingAssessmentSessionDateFrom)
            : null,
          scoringAssessmentSessionDateFrom: schedule.scoringAssessmentSessionDateFrom
            ? getLocalISODate(schedule.scoringAssessmentSessionDateFrom)
            : null,
        };
      },
    );
    const id = isExaminerView ? undefined : sheetId;
    updateProcessesSchedule(schedulesUpdateData, id).then(
      () => {
        openAlert('success');
        setEdited(false);
      },
      () => {
        openAlert('error');
      },
    );
  };

  useEffect(() => {
    isExaminerView
      ? getProcessesScheduleBySession()
          .then((res: ExamTaskGroupProcessesSchedule[]) => {
            setSchedules(res);
            setIsDataLoaded(true);
          })
          .catch((e) => console.error({ e }))
          .finally(() => setIsLoading(false))
      : getProcessesScheduleBySheetId(sheetId)
          .then((res: ExamTaskGroupProcessesSchedule[]) => {
            setSchedules(res);
            setIsDataLoaded(true);
          })
          .catch((e) => console.error({ e }))
          .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    onChange && onChange(edited);
  }, [edited]);

  const isNullishSchedule = () => {
    const schedule = Array.from(Object.values(schedules[0]));
    return schedule.every((e) => e === null);
  };

  if (!sheetId || (isDataLoaded && (!schedules.length || isNullishSchedule()))) {
    return (
      <>
        <div className={styles.unconfiguredHeader}>Harmonogram procesów</div>
        <div className={styles.unconfiguredSheet}>
          <InfoOutlinedIcon htmlColor="#BCBEDC" className={styles.unconfiguredSheetIcon} />
          <div className={styles.unconfiguredText}>
            Należy zakończyć konfigurację arkusza, by móc ustawiać daty i godziny rozpoczęcia/zakończenia procesów w
            sesji oceniania.
          </div>
        </div>
      </>
    );
  } else {
    return (
      <>
        <InputTable<ExamTaskGroupProcessesSchedule>
          columns={columns}
          parameters={schedules === undefined ? [] : schedules}
          items={schedules === undefined ? [] : schedules}
          keyParam={'examTaskGroupId'}
          invalidType={invalidType}
          headerText={t('sessionsParameters:sessionsProcessSchedule.header')}
          onCancel={onClose}
          onSave={handleSave}
          getValidationMessage={getValidationMessage}
          getRowComponent={getRowComponent}
          edited={edited}
          emptyMessage="Brak wyników"
          isLoading={isLoading}
        />
        {dateTimePickerAnchor && activeSchedule && activeProcess !== undefined && (
          <DateTimePickerOneMenu
            onChange={(newDate: Date | undefined) => updateProcessDate(newDate ?? null, activeSchedule, activeProcess)}
            selectedDateFrom={
              getDateByProcess(activeSchedule, activeProcess) ?? activeSchedule.assessmentSessionDateFrom ?? new Date()
            }
            pickerAnchor={dateTimePickerAnchor}
            onClose={clearDatePickerData}
            singleMode
            minDate={activeSchedule.assessmentSessionDateFrom ?? undefined}
            maxDate={activeSchedule.assessmentSessionDateTo ?? undefined}
            automaticDateTimeExitFlow={true}
          />
        )}
      </>
    );
  }
};
